import React from "react";
import { ApiContext } from "../common/context/ApiContext";
import { useLiveData } from "../common/utils";
import { Account, AccountContext } from "../common/context/AccountContext";
import { TabEnum, TabsContext } from "../common/context/TabsContext";
import { useLoadingIndicator } from "../common/context/LoadingIndicatorContext";
import { useTranslation } from "react-i18next";
import { Row } from "react-bootstrap";
import { BackNav, FlexRow, PageContainer } from "../common/CommonStyles";
import { Link } from "react-router-dom";
import { SchoolRenewalInfo, SchoolRenewalList } from "../common/api";

// A school is flagged as due soon once its end date is within this many days.
const DUE_SOON_DAYS = 30;

// "YYYY-MM-DD" parsed as local midnight. new Date("2026-09-28") would parse as UTC
// midnight, which renders as the previous day west of Greenwich and throws off the
// day count by one.
const parseLocalDate = (value: string): Date => {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const startOfToday = (): Date => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

// The date that actually drives the renewal: whichever of the two comes first.
const soonestEndDate = (school: SchoolRenewalInfo): Date | null => {
  const dates = [school.pilot_end_date, school.access_end_date]
    .filter((value): value is string => value !== null)
    .map(parseLocalDate);
  if (dates.length === 0) {
    return null;
  }
  return dates.reduce((a, b) => (a < b ? a : b));
};

const daysUntil = (date: Date): number =>
  Math.round(
    (date.getTime() - startOfToday().getTime()) / (1000 * 60 * 60 * 24)
  );

export const SchoolRenewalsPage: React.FC = () => {
  const { t } = useTranslation();
  const { getSchoolRenewalList } = React.useContext(ApiContext);
  const { accountLiveData } = React.useContext(AccountContext);
  const { setCurrentTab } = React.useContext(TabsContext);
  const [account, setAccount] = React.useState<Account>();
  const { startLoading, stopLoading } = useLoadingIndicator(true);
  const listFetchedForUserVersion = React.useRef<number | null>(null);
  const fetchJobForUserVersion = React.useRef<number | null>(null);
  const [renewalList, setRenewalList] =
    React.useState<SchoolRenewalList | null>(null);

  React.useEffect(() => {
    setCurrentTab(TabEnum.SchoolRenewals);
  }, [setCurrentTab]);

  useLiveData(
    React.useCallback(async (account: Account) => {
      if (account.hasUserAndMaybeProfile()) {
        setAccount(account);
      }
    }, []),
    accountLiveData
  );

  React.useEffect(() => {
    (async () => {
      if (!account) {
        return;
      }
      if (
        account.userCurrentVersion !== listFetchedForUserVersion.current &&
        account.userCurrentVersion !== fetchJobForUserVersion.current
      ) {
        startLoading();
        const expectedVersion = (fetchJobForUserVersion.current =
          account.userCurrentVersion);
        const schools = await getSchoolRenewalList();
        if (expectedVersion !== fetchJobForUserVersion.current) {
          return;
        }
        setRenewalList(schools);
        listFetchedForUserVersion.current = fetchJobForUserVersion.current;
        stopLoading();
      }
    })();
  }, [account, getSchoolRenewalList, startLoading, stopLoading]);

  if (!renewalList) {
    return <></>;
  }

  const formatDate = (value: string | null) => {
    if (value === null) {
      return <span className="text-muted">—</span>;
    }
    return parseLocalDate(value).toLocaleDateString();
  };

  return (
    <PageContainer>
      <Row className="align-items-center">
        <FlexRow>
          <BackNav label={t("generic_nav_back")} />
        </FlexRow>
      </Row>
      <div className="text-center mt-4 mb-3">
        <h3>{t("school_renewals_title")}</h3>
      </div>
      {renewalList.schools.length === 0 ? (
        <div className="text-center text-muted">
          {t("school_renewals_empty")}
        </div>
      ) : (
        <div>
          <table className="table align-middle">
            <thead>
              <tr>
                <th scope="col">{t("school_renewals_school")}</th>
                <th scope="col">{t("school_renewals_district")}</th>
                <th scope="col">{t("school_renewals_pilot_end")}</th>
                <th scope="col">{t("school_renewals_access_end")}</th>
                <th scope="col">{t("school_renewals_days")}</th>
                <th scope="col">{t("school_renewals_account_manager")}</th>
                <th scope="col" className="text-end">
                  {t("school_renewals_active")}
                </th>
              </tr>
            </thead>
            <tbody>
              {renewalList.schools.map((info) => {
                const endDate = soonestEndDate(info);
                const days = endDate === null ? null : daysUntil(endDate);
                let daysClassName = "";
                if (days !== null && days < 0) {
                  daysClassName = "text-danger fw-bold";
                } else if (days !== null && days <= DUE_SOON_DAYS) {
                  daysClassName = "text-warning fw-bold";
                }
                return (
                  <tr key={info.id}>
                    <td>
                      <Link
                        className="text-decoration-none"
                        to={`/schools/dashboard/?school_id=${info.id}`}
                      >
                        {info.name}
                      </Link>
                    </td>
                    <td>{info.district_name}</td>
                    <td>{formatDate(info.pilot_end_date)}</td>
                    <td>{formatDate(info.access_end_date)}</td>
                    <td className={daysClassName}>
                      {days === null ? (
                        <span className="text-muted">—</span>
                      ) : days < 0 ? (
                        t("school_renewals_expired_days", { val: -days })
                      ) : (
                        t("school_renewals_remaining_days", { val: days })
                      )}
                    </td>
                    <td>
                      {info.account_manager_email ?? (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="text-end">{info.is_active ? "✅" : "❌"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </PageContainer>
  );
};
